import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form2220 = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 2220 Guide: Underpayment of Estimated Tax | TaxZerone"
          description="Learn how to file Form 2220 for underpaid estimated taxes for corporations, Form 990-T and Form 990-PF filers, with step-by-step instructions using TaxZerone."
          keywords=""
          canonical="resources/tax-exempt/form-2220-underpayment-estimated-tax-corporations/"
          ogType="article"
          jsonLdPricing="89.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          Form 2220,{' '}
          <span className="highlight-container">
            <span className="highlight">Underpayment of Estimated Tax </span>
          </span>{' '}
          <br />
          by Corporations
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/form-2220-underpayment-estimated-tax-corporations/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 2220 at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          What Is Form 2220? Form 2220, Underpayment of
                          Estimated Tax by Corporations,
                        </strong>{' '}
                        is used to determine whether an organization owes a{' '}
                        <strong style={{ fontWeight: 600 }}>
                          penalty for underpaying estimated tax{' '}
                        </strong>
                        and to calculate the penalty amount.
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          Who Uses It? Tax-exempt organizations subject to
                          unrelated business income tax (UBIT),{' '}
                        </strong>
                        private foundations, and corporations may use Form 2220
                        when estimated-tax underpayment rules apply.
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          What Is Reported?{' '}
                        </strong>
                        Form 2220 calculates{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          required estimated-tax installments, underpayments,
                          applicable payment dates,
                        </strong>
                        and the resulting{' '}
                        <strong style={{ fontWeight: 600 }}>
                          underpayment penalty.
                        </strong>
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          Supported Tax Returns:{' '}
                        </strong>{' '}
                        Form 2220 may be used with{' '}
                        <Link
                          href="https://www.taxzerone.com/resources/tax-exempt/form-990-t-instructions/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Form 990-T
                        </Link>
                        ,{' '}
                        <Link
                          href="https://www.taxzerone.com/resources/tax-exempt/form-990-pf-instructions/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Form 990-PF
                        </Link>
                        <strong style={{ fontWeight: 600 }}> Form 1120 </strong>
                        and other applicable corporate tax returns, depending on
                        the organization's estimated-tax requirements.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>

                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Table of Contents
                    </strong>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is Form 2220?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who must file Form 2220?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#how" sx={{ color: '#0202ff' }}>
                          How to file Form 2220?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Commonly Asked Questions about Form 2220
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="what"
                >
                  What is Form 2220?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Form 2220</span> is a tax
                  form used to calculate any underpayment of estimated tax by
                  corporations. Corporations are required to pay their taxes
                  throughout the year in quarterly installments. If these
                  payments are not made on time or in the correct amounts, an
                  underpayment penalty may apply, and Form 2220 helps determine
                  the amount of this penalty.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4, fontStyle: 'italic' }}>
                  This form is commonly used with{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-t/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 990-T
                  </Link>
                  , which tax-exempt organizations file to report unrelated
                  business taxable income, and{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-pf/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 990-PF
                  </Link>
                  , which private foundations file to report their annual
                  financial activity.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="who"
                >
                  Who must file Form 2220?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Corporations, tax-exempt organizations, and private
                  foundations that underpay their estimated taxes are required
                  to file <span style={{ fontWeight: 600 }}>Form 2220</span>.
                  This includes:
                </Typography>

                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}> Corporations:</strong>{' '}
                    Any corporation that did not pay enough estimated tax
                    throughout the year must file{' '}
                    <span style={{ fontWeight: 600 }}>Form 2220</span> to
                    calculate the underpayment penalty.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      Tax-Exempt Organizations:
                    </strong>{' '}
                    Organizations that file{' '}
                    <span style={{ fontWeight: 600 }}>Form 990-T</span> to
                    report unrelated business taxable income (UBTI) and fail to
                    make timely estimated tax payments must file Form 2220 to
                    compute the penalty for underpayment.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      Private Foundations:
                    </strong>{' '}
                    Foundations that file{' '}
                    <span style={{ fontWeight: 600 }}>Form 990-PF</span> must
                    also use Form 2220 if they underpay their estimated tax
                    obligations.
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Corporations or organizations may not need to file the form if
                  they qualify for an exception to the penalty, such as meeting
                  the safe harbor rule or if their total tax liability is below
                  a certain threshold.
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 0, lineHeight: 1.2 }}
                      >
                        Choose TaxZerone for a seamless e-filing experience and
                        stay compliant with the IRS.
                      </Typography>
                      <Typography variant="body2">
                        Your journey to stress-free tax filing starts here!
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 120, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form2220`}
                      >
                        E-file Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                {/* <Alert severity="warning" icon={false} sx={{ mb: 4 }} >
                  <strong style={{fontWeight:600}} >Note:</strong> The deadline is extended to the next business day
                  if the due date falls on a weekend or a legal holiday.
                </Alert> */}
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 2,
                    scrollMarginTop: 83,
                  }}
                  id="how"
                >
                  How to file Form 2220?
                </Typography>
                <picture>
                  <source srcSet="/assets/images/how-to-file-form-2220-img.webp" />
                  <source srcSet="/assets/images/how-to-file-form-2220-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/how-to-file-form-2220-img.png"
                    loading="lazy"
                    alt="Form 2220, Underpayment of Estimated Tax by Corporations "
                    width={759}
                    height={159}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 3,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>
                    Form 2220, Underpayment of Estimated Tax by Corporations
                  </span>{' '}
                  contains the organization’s basic identification details and
                  an important IRS filing note.
                </Typography>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Name
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    legal name of the corporation or exempt organization
                  </span>{' '}
                  exactly as shown on the related tax return, such as{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-t/"
                    sx={{ color: '#0202ff' }}
                  >
                    IRS Form 990-T
                  </Link>{' '}
                  or{' '}
                  <Link
                    href="/resources/tax-exempt/form-990-pf-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    990-PF
                  </Link>
                  .
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Employer Identification Number (EIN)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the organization’s{' '}
                  <span style={{ fontWeight: 600 }}>9-digit EIN</span> used for
                  IRS tax filings. The EIN on Form 2220 should match the EIN
                  entered on the related return.
                </Typography>
                <Alert
                  severity="info"
                  icon={
                    <EditNoteOutlinedIcon
                      sx={{ fontSize: 30 }} // increase icon size
                    />
                  }
                  sx={{
                    fontSize: 13,
                    mt: 1,
                    mb: 3,
                    borderLeft: '3px solid rgb(0, 78, 102)',
                  }}
                >
                  <span style={{ fontWeight: 600 }}>Note:</span> Generally,
                  organizations are not required to file Form 2220 because the
                  IRS can calculate the penalty separately. However, if the
                  organization chooses to calculate the penalty itself, enter
                  the amount from page 2, line 38, on the tax return. Do not
                  attach Form 2220 unless one of the Part II exceptions applies.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '22px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part I-Required Annual Payment
                </Typography>
                <picture>
                  <source srcSet="/assets/images/part1-required-annual-payment-img.webp" />
                  <source srcSet="/assets/images/part1-required-annual-payment-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/part1-required-annual-payment-img.png"
                    loading="lazy"
                    alt="Form 2220 Part I-Required Annual Payment Line1 to Line5"
                    width={759}
                    height={227}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 3,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1
                </Typography>
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 0, '& ul, li': { mb: 1.5 } }}
                >
                  <li>
                    Organizations filing Form 990-T should generally enter the
                    total tax amount from the applicable tax return line used to
                    report income tax liability. If the reported tax includes
                    Corporate Alternative Minimum Tax (CAMT), do not include the
                    CAMT amount on line 1 of Form 2220. Instead, write “CAMT”
                    and show the amount in brackets on the dotted line beside
                    line 1.
                  </li>
                  <li>
                    For exempt organizations and other entities, refer to the
                    applicable return instructions, including{' '}
                    <Link
                      href="/resources/tax-exempt/form-990-t-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 990-T
                    </Link>{' '}
                    and{' '}
                    <Link
                      href="/resources/tax-exempt/form-990-pf-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 990-PF
                    </Link>
                    , to determine the correct total tax amount for estimated
                    tax purposes.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 2a
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter any{' '}
                  <span style={{ fontWeight: 600 }}>
                    personal holding company tax
                  </span>{' '}
                  included in line 1. Use the amount reported on Schedule PH
                  (Form 1120), line 26.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 2b
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter any look-back interest included in line 1 under section
                  460(b)(2) for completed long-term contracts or under section
                  167(g) for depreciation calculated using the income forecast
                  method.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 2c
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    Corporate Alternative Minimum Tax (CAMT)
                  </span>{' '}
                  amount reported on the applicable income tax return, including
                  Form 990-T.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 2d
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add the amounts from lines 2a through 2c and enter the total
                  on line 2d.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Subtract line 2d from line 1. If the result is{' '}
                  <span style={{ fontWeight: 600 }}>less than $500</span>, the
                  organization is not required to complete or file Form 2220
                  because no underpayment penalty applies.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the tax reported on the organization’s 2024 income tax
                  return, figured in the same manner as line 3. For Form 990-T
                  filers, use the applicable tax amount from the prior-year
                  return.
                </Typography>
                <Alert
                  severity="warning"
                  sx={{
                    fontSize: 13,
                    mt: 1,
                    mb: 3,
                    borderLeft: '3px solid rgb(102, 75, 0)',
                  }}
                >
                  <span style={{ fontWeight: 600 }}>Caution:</span> Skip line 4
                  and enter the amount from line 3 on line 5 if the organization
                  had no 2024 tax liability or if the 2024 tax year was less
                  than 12 months.
                </Alert>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the smaller of line 3 or line 4 as the required annual
                  payment. If line 4 was skipped, enter the amount from line 3.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '22px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part II-Reasons for Filing
                </Typography>
                <picture>
                  <source srcSet="/assets/images/part2-reasons-for-filing-img.webp" />
                  <source srcSet="/assets/images/part2-reasons-for-filing-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/part2-reasons-for-filing-img.png"
                    loading="lazy"
                    alt="Form 2220 Part II-Reasons for Filing Line6 to Line8 "
                    width={759}
                    height={81}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 3,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 6
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Check this box if the organization is using the adjusted
                  seasonal installment method to calculate estimated tax
                  payments. This method may help reduce underpayment penalties
                  when income is earned unevenly during the year.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 7
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Check this box if the organization is using the annualized
                  income installment method to calculate estimated tax payments
                  based on income earned during specific periods of the year.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 8
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Check this box if the organization qualifies as a large
                  corporation and is calculating its first required installment
                  using the prior year’s tax amount. Generally, a large
                  corporation is one that had a taxable income of $1 million or
                  more during any of the previous three tax years.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '22px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part III-Figuring the Underpayment
                </Typography>
                <picture>
                  <source srcSet="/assets/images/part3-figuring-the-underpayment-img.webp" />
                  <source srcSet="/assets/images/part3-figuring-the-underpayment-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/part3-figuring-the-underpayment-img.png"
                    loading="lazy"
                    alt="Form 2220 Part III-Figuring the Underpayment Line9 to Line18"
                    width={759}
                    height={448}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 3,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 9
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the installment due dates for the organization’s tax
                  year. Generally, estimated tax installments are due on the{' '}
                  <span style={{ fontWeight: 600 }}>
                    15th day of the 4th, 6th, 9th, and 12th months
                  </span>{' '}
                  of the tax year.{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-pf/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 990-PF
                  </Link>{' '}
                  filers should use the{' '}
                  <span style={{ fontWeight: 600 }}>
                    5th month instead of the 4th month
                  </span>{' '}
                  for the first installment.
                </Typography>
                <Alert
                  severity="info"
                  icon={
                    <EditNoteOutlinedIcon
                      sx={{ fontSize: 30 }} // increase icon size
                    />
                  }
                  sx={{
                    fontSize: 13,
                    mt: 1,
                    mb: 3,
                    borderLeft: '3px solid rgb(0, 78, 102)',
                  }}
                >
                  <span style={{ fontWeight: 600 }}>Note:</span> More than one
                  column may have the same due date if the IRS provides filing
                  or payment relief, such as disaster relief extensions.
                </Alert>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 10
                </Typography>
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 0, '& ul, li': { mb: 1.5 } }}
                >
                  <li>
                    Enter the organization’s required installment amounts. If
                    the adjusted seasonal installment method or annualized
                    income installment method is used, enter the amounts from
                    Schedule A, line 38.
                  </li>
                  <li>
                    Large corporations may have special calculation rules for
                    determining installment amounts based on the prior year’s
                    tax liability.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 11
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter all estimated tax payments made for the tax year,
                  including any overpayment from the prior year’s return that
                  was applied to the current year’s estimated tax.
                </Typography>
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 0, '& ul, li': { mb: 1.5 } }}
                >
                  <li>
                    In column (a), include payments made by the first
                    installment due date.
                  </li>
                  <li>
                    In columns (b), (c), and (d), include payments made between
                    the due dates for each installment period.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If a due date falls on a weekend or legal holiday, payments
                  made on the next business day are treated as timely.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 12
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter any overpayment amount from line 18 of the previous
                  column.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 13
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add the amounts from lines 11 and 12 and enter the total.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 14
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the combined total of lines 16 and 17 from the previous
                  column.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 15
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Subtract line 14 from line 13. If the result is zero or less,
                  enter “0.”
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 16
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If line 15 is zero, subtract line 13 from line 14 and enter
                  the result. Otherwise, enter “0.”
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 17
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If any column in line 17 shows an underpayment amount,
                  complete Part IV to calculate the underpayment penalty.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 18
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If line 15 is greater than line 10, subtract line 10 from line
                  15 and enter the overpayment amount. Carry this amount to line
                  12 of the next column.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '22px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part IV-Figuring the Penalty
                </Typography>
                <picture>
                  <source srcSet="/assets/images/part4-figuring-the-penalty-img.webp" />
                  <source srcSet="/assets/images/part4-figuring-the-penalty-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/part4-figuring-the-penalty-img.png"
                    loading="lazy"
                    alt="Form 2220 Part IV-Figuring the Penalty Line19 to Line38"
                    width={759}
                    height={747}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 3,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 19
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the earliest of either the payment date or the{' '}
                  <span style={{ fontWeight: 600 }}>
                    15th day of the fourth month
                  </span>{' '}
                  of the following year from the close of the tax year. Special
                  rules apply to certain filers.
                </Typography>
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 0, '& ul, li': { mb: 1.5 } }}
                >
                  <li>C corporations with tax years ending June 30 and</li>
                  <li>
                    S corporations use the 3rd month instead of the 4th month.
                  </li>
                  <li>
                    Form 990-T and Form 990-PF filers use the 5th month after
                    the end of the tax year.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 20
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the number of days from the installment due date shown
                  on line 9 to the ending date entered on line 19.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 21
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the number of days in line 20 that fall after 4/15/2025
                  and before 7/1/2025.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 22
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Calculate the penalty for the period shown on line 21 using
                  the applicable IRS underpayment interest rate.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 23
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the number of days in line 20 that fall after 6/30/2025
                  and before 10/1/2025.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 24
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Calculate the penalty for the period shown on line 23 using
                  the applicable IRS interest rate.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 25
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the number of days in line 20 that fall after 9/30/2025
                  and before 1/1/2026.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 26
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Calculate the penalty for the period shown on line 25 using
                  the applicable IRS interest rate.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 27
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the number of days in line 20 that fall after 12/31/2025
                  and before 4/1/2026.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 28
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Calculate the penalty for the period shown on line 27 using
                  the applicable IRS interest rate.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 29
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the number of days in line 20 that fall after 3/31/2026
                  and before 7/1/2026.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 30
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Calculate the penalty for the period shown on line 29 using
                  the applicable IRS interest rate.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 31
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the number of days in line 20 that fall after 6/30/2026
                  and before 10/1/2026.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 32
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Calculate the penalty for the period shown on line 31 using
                  the applicable IRS interest rate.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 33
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the number of days in line 20 that fall after 9/30/2026
                  and before 1/1/2
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 34
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Calculate the penalty for the period shown on line 33 using
                  the applicable IRS interest rate.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 35
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the number of days in line 20 that fall after 12/31/2026
                  and before 3/16/2027.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 36
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Calculate the penalty for the period shown on line 35 using
                  the applicable IRS interest rate.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 37
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add the penalty amounts from lines 22, 24, 26, 28, 30, 32, 34,
                  and 36.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 38
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the total penalty amount from line 37. Report this
                  amount on the applicable penalty line of the organization’s
                  return, such as Form 990-T or Form 990-PF.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 2,
                    scrollMarginTop: 83,
                  }}
                  id="how"
                >
                  Schedule A-Adjusted Seasonal Installment Method and Annualized
                  Income Installment Method
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '22px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part I-Adjusted Seasonal Installment Method
                </Typography>
                <picture>
                  <source srcSet="/assets/images/schedule-a-part1-img.webp" />
                  <source srcSet="/assets/images/schedule-a-part1-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/schedule-a-part1-img.png"
                    loading="lazy"
                    alt="Form 2220, Schedule A, Part I-Adjusted Seasonal Installment Method Line1 to Line10"
                    width={759}
                    height={517}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 3,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  A business entity is eligible to make use of this adjustment
                  only when the six consecutive months base-period percentage is{' '}
                  <span style={{ fontWeight: 600 }}>70 percent or more</span> in
                  relation to the tax year. The base-period percentage of six
                  consecutive months is calculated based on the average of the
                  percentages of taxable incomes in those six months over the
                  last three tax years.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The seasonal installment method is often favorable for
                  businesses with seasonal revenues. This is because this
                  adjustment will help decrease penalties for underpayment of
                  taxes.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 1a–1c
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the organization’s taxable income for the specified
                  periods for each tax year beginning in{' '}
                  <span style={{ fontWeight: 600 }}>2022, 2023, and 2024</span>.
                  Use the income amounts for the{' '}
                  <span style={{ fontWeight: 600 }}>
                    first 3, 5, 8, and 11 months
                  </span>{' '}
                  of each tax year.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the organization has extraordinary items, special
                  calculation rules may apply. Do not include any de minimis
                  items that are reported on line 9b.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 3a–3c
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the organization’s taxable income for the specified
                  periods for tax years beginning in{' '}
                  <span style={{ fontWeight: 600 }}>2022, 2023, and 2024</span>.
                  Use the income amounts for the{' '}
                  <span style={{ fontWeight: 600 }}>
                    first 4, 6, and 9 months
                  </span>
                  , and for the entire tax year.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Divide each amount entered on line 1a by the amount in column
                  (d) of line 3a and enter the results in the applicable
                  columns.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Divide each amount entered on line 1b by the amount in column
                  (d) of line 3b and enter the results in the applicable
                  columns.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 6
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Divide each amount entered on line 1c by the amount in column
                  (d) of line 3c and enter the results in the applicable
                  columns.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 7
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add the amounts from lines 4 through 6 and enter the total for
                  each column.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 8
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Divide the amount on line 7 by 3 and enter the result.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 9b
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter any extraordinary items, net operating loss deductions,
                  or section 481(a) adjustments for the applicable period. Also
                  include any de minimis items the organization chooses to
                  exclude from line 2.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 10
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Figure the tax on the amount entered on line 9c using the
                  applicable tax calculation instructions for the organization’s
                  return, such as{' '}
                  <span style={{ fontWeight: 600 }}>Form 990-T</span> or{' '}
                  <span style={{ fontWeight: 600 }}>Form 990-PF</span>.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/line11a-19line-img.webp" />
                  <source srcSet="/assets/images/line11a-19line-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/line11a-19line-img.png"
                    loading="lazy"
                    alt=" Form 2220, Schedule A, Part I-Adjusted Seasonal Installment Method Line11 to Line19"
                    width={759}
                    height={305}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 3,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 11a
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Divide the amounts in columns (a) through (c) on line 3a by
                  the amount in column (d) on line 3a.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 11b
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Divide the amounts in columns (a) through (c) on line 3b by
                  the amount in column (d) on line 3b.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 11c
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Divide the amounts in columns (a) through (c) on line 3c by
                  the amount in column (d) on line 3c.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 12
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add the amounts from lines 11a through 11c for each column.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 13
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Divide the amount on line 12 by 3 and enter the result.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 14
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Multiply the amount in each column of line 10 by the
                  corresponding amount on line 13. In column (d), enter the
                  amount from line 10, column (d).
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 15
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter any alternative minimum tax (AMT) for each payment
                  period, if applicable. Form 990-T trust filers should
                  calculate AMT using Schedule I (Form 1041).
                </Typography>
                <Alert
                  severity="info"
                  icon={
                    <EditNoteOutlinedIcon
                      sx={{ fontSize: 30 }} // increase icon size
                    />
                  }
                  sx={{
                    fontSize: 13,
                    mt: 1,
                    mb: 3,
                    borderLeft: '3px solid rgb(0, 78, 102)',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Note:</strong> Applicable
                  corporations may exclude Corporate Alternative Minimum Tax
                  (CAMT) when calculating the required annual payment on Form
                  2220.
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 16
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter any other taxes owed for each payment period. Include
                  the same taxes used to calculate Form 2220, Part I, line 1,
                  such as base erosion minimum tax, if applicable.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 17
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add the amounts from lines 14 through 16 and enter the total.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 18
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the credits allowed for each payment period. Use the
                  same types of credits reported on Form 2220, Part I, lines 1
                  and 2c.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 19
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Subtract line 18 from line 17. If the result is zero or less,
                  enter “0.”
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '22px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part II-Annualized Income Installment Method
                </Typography>
                <picture>
                  <source srcSet="/assets/images/part2-annualized-income-installment-method-img.webp" />
                  <source srcSet="/assets/images/part2-annualized-income-installment-method-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/part2-annualized-income-installment-method-img.png"
                    loading="lazy"
                    alt="Form 2220, Schedule A Part II-Annualized Income Installment Method Line20 to Line31"
                    width={759}
                    height={377}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 3,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 20
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the annualization periods for{' '}
                  <span style={{ fontWeight: 600 }}>columns (a)</span> through
                  <span style={{ fontWeight: 600 }}>(d)</span> based on the
                  option selected in the Form 2220 instructions. For example,
                  organizations using Option 1 should enter 2, 4, 7, and 10 in
                  columns (a) through (d).
                </Typography>
                <Alert
                  severity="warning"
                  sx={{
                    fontSize: 13,
                    mt: 1,
                    mb: 3,
                    borderLeft: '3px solid rgb(102, 75, 0)',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Caution:</strong> Option 1
                  or Option 2 can be used only if Form 8842 was filed by the due
                  date of the first required estimated tax installment. Once
                  elected, the option cannot be changed for that tax year.
                  Option 2 is not available for tax-exempt organizations or
                  private foundations filing{' '}
                  <Link
                    href="/resources/tax-exempt/form-990-t-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 990-T
                  </Link>{' '}
                  or{' '}
                  <Link
                    href="/resources/tax-exempt/form-990-pf-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 990-PF
                  </Link>
                  .
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 21
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the taxable income received during each annualization
                  period listed on line 20. Do not include de minimis
                  extraordinary items that will be reported on line 23b.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 22
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the annualization amounts for each column based on the
                  option used for line 20, as provided in the Form 2220
                  instructions.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 23a
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Multiply the taxable income on line 21 by the annualization
                  amount on line 22.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 23b
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter any extraordinary items, net operating loss deductions,
                  or section 481(a) adjustments applicable to the annualization
                  period. Also include any de minimis extraordinary items
                  excluded from line 21.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 23c
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add lines 23a and 23b and enter the total.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 24
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Figure the tax on the amount entered on line 23c using the
                  applicable tax instructions for the organization’s return,
                  such as Form 990-T or Form 990-PF.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 25
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter any alternative minimum tax (AMT) for each annualization
                  period, if applicable. Form 990-T trust filers should
                  calculate AMT using{' '}
                  <Link
                    href="/resources/tax-exempt/1041-schedule-i-amt/"
                    sx={{ color: '#0202ff' }}
                  >
                    Schedule I (Form 1041)
                  </Link>
                  .
                </Typography>
                <Alert
                  severity="info"
                  icon={
                    <EditNoteOutlinedIcon
                      sx={{ fontSize: 30 }} // increase icon size
                    />
                  }
                  sx={{
                    fontSize: 13,
                    mt: 1,
                    mb: 3,
                    borderLeft: '3px solid rgb(0, 78, 102)',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Note:</strong> Applicable
                  corporations may exclude certain Corporate Alternative Minimum
                  Tax (CAMT) amounts when calculating the required annual
                  payment.
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 26
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter any other taxes owed for each annualization period.
                  Include the same taxes used to calculate Form 2220, Part I,
                  line 1, such as base erosion minimum tax, if applicable.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 27
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add the amounts from lines 24 through 26 and enter the total
                  tax.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 28
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the credits allowed for each annualization period. Do
                  not annualize the credits themselves, but annualize any income
                  or deductions used to calculate those credits.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 29
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Subtract line 28 from line 27. If the result is zero or less,
                  enter “0.”
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 30
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the applicable percentage for each installment period.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 31
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Multiply line 29 by line 30 and enter the result.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '22px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part III-Required Installments
                </Typography>
                <picture>
                  <source srcSet="/assets/images/part3-required-installments-img.webp" />
                  <source srcSet="/assets/images/part3-required-installments-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/part3-required-installments-img.png"
                    loading="lazy"
                    alt="Form 2220, Schedule A, Part III-Required Installments Line32 to Line38"
                    width={759}
                    height={275}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 3,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 32
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If only Part I or Part II was completed, enter the amount from
                  line 19 or line 31 for each column. If both parts were
                  completed, enter the smaller amount from line 19 or line 31.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 33
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add the amounts from all previous columns of line 38 before
                  completing the current column.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 34
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Subtract line 33 from line 32. If the result is zero or less,
                  enter “0.”
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 35
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter 25% of the amount from Form 2220, page 1, line 5, in
                  each column. Large corporations may need to use special rules
                  when figuring this amount.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 36
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Subtract line 38 of the previous column from line 37 of the
                  previous column.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 37
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add lines 35 and 36 and enter the total.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 500,
                    mb: 0.5,
                    textAlign: 'left',
                    lineHeight: 1.5,
                  }}
                >
                  Line 38
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the smaller of line 34 or line 37 as the required
                  installment amount. Also enter this amount on Form 2220, page
                  1, line 10.
                </Typography>
                <Alert
                  severity="info"
                  icon={false}
                  sx={{ borderRadius: 2, mb: 2 }}
                >
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The above given information is based on the IRS
                        guidelines. For more details, please refer to the{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i2220.pdf"
                          sx={{ color: '#0202ff' }}
                        >
                          Instructions for Form 2220
                        </Link>
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    mb: 2,
                    border: '1px solid #ccc',
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                    Embrace the Future of Tax Filing – Choose TaxZerone Today!
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    Make your e-filing process simple by clicking the button
                    below.
                  </Typography>

                  <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      textTransform: 'none',
                      boxShadow: 'none',
                      borderRadius: 2,
                    }}
                    size="large"
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form2220`}
                  >
                    Start E-filing Now
                  </Button>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ my: 0 }}>
        <Grid
          container
          spacing={2}
          mx={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 0 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
            className="responsive"
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
                scrollMarginTop: 83,
              }}
              id="common"
            >
              Commonly Asked Questions
            </Typography>
            <Box
              component="div"
              sx={{ mb: 5 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. When is a tax-exempt organization required to file Form
                  2220?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography variant="body1" sx={{ mt: 1, mb: 1 }}>
                      Tax-exempt organizations that file{' '}
                      <Link
                        href="/tax-exempt/e-file-irs-form-990-t/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 990-T
                      </Link>{' '}
                      to report unrelated business income must file <br />
                      <span style={{ fontWeight: 600 }}>Form 2220</span> if they
                      underpay their estimated taxes. This form helps calculate
                      the penalty for any underpayment of taxes related to
                      unrelated business income.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. How do estimated tax payments work for corporations and
                  tax-exempt organizations?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography variant="body1" sx={{ mb: 1, mt: 1 }}>
                      Corporations and tax-exempt organizations are required to
                      make quarterly estimated tax payments throughout the year.
                      If these payments are not made on time or in the correct
                      amount, the IRS may impose an underpayment penalty, which
                      is calculated using Form 2220.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. What happens if a corporation underpays its estimated tax?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text" sx={{ mt: 1 }}>
                    If a corporation underpays its estimated tax, it may be
                    subject to a penalty. The penalty is calculated based on the
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      amount of the underpayment
                    </span>{' '}
                    and the{' '}
                    <span style={{ fontWeight: 600 }}>
                      length of time it remains unpaid
                    </span>
                    .
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default Form2220;
